import React, { useState } from 'react';

export default function PlanejamentoChart({ colunasMeses, metasEditaveis, somaMensal }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Helper para obter a chave do status no banco
  const getDbStatusKey = (col) => `${col}_status`;

  // Helper para formatar o período do label do eixo X (ex: "jan/26", "2027")
  const formatChartLabel = (periodo) => {
    if (!periodo) return '';
    if (periodo.startsWith('ano')) {
      return periodo.replace('ano', '');
    }
    if (periodo.includes('-')) {
      const [ano, mes] = periodo.split('-');
      const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
      return `${meses[parseInt(mes) - 1]}/${ano.slice(-2)}`;
    }
    return periodo; // Retorna "2027", "2028", etc.
  };

  // Helper para formatar por extenso no tooltip
  const formatTooltipLabel = (periodo) => {
    if (!periodo) return '';
    if (periodo.startsWith('ano')) {
      return `Ano ${periodo.replace('ano', '')}`;
    }
    if (periodo.includes('-')) {
      const [ano, mes] = periodo.split('-');
      const meses = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      return `${meses[parseInt(mes) - 1]} de ${ano}`;
    }
    return `Ano ${periodo}`;
  };

  // Determinar se uma coluna (mês/ano) é Consolidada
  const isColConsolidado = (col) => {
    if (!metasEditaveis || metasEditaveis.length === 0) return false;
    return metasEditaveis.some(row => row[getDbStatusKey(col)] === 'Consolidado');
  };

  // Processar dados dos pontos e calcular o acumulado progressivamente
  let runningSum = 0;
  const pontos = colunasMeses.map((col, index) => {
    let valorMensal = somaMensal[col] || 0;
    
    // Se for uma coluna legada de ano (ex: ano2027), verificar se os meses correspondentes já foram detalhados na tabela (se a soma dos meses > 0)
    if (col.startsWith('ano')) {
      const anoStr = col.replace('ano', '');
      let sumMeses = 0;
      for(let m = 1; m <= 12; m++) {
        sumMeses += (somaMensal[`${anoStr}-${String(m).padStart(2, '0')}`] || 0);
      }
      if (sumMeses > 0) {
        valorMensal = sumMeses;
      }
    }

    const isConsol = isColConsolidado(col);
    runningSum += valorMensal;
    return {
      col,
      index,
      label: formatChartLabel(col),
      tooltipLabel: formatTooltipLabel(col),
      valorMensal,
      valorAcumulado: runningSum,
      isConsolidado: isConsol
    };
  });

  // Dimensões do SVG
  const width = 1100;
  const height = 320;
  const paddingLeft = 70;
  const paddingRight = 30;
  const paddingTop = 40;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  // Escalar valores com base no maior acumulado
  const maxAccumulated = runningSum;
  const steps = 8;
  // Calcula valor de incremento arredondado para dezenas/milhares (ex: passos de 20.000)
  const stepValue = Math.ceil((maxAccumulated * 1.15) / steps / 1000) * 1000 || 10000;
  const yMax = stepValue * steps;

  const divisor = pontos.length > 1 ? pontos.length - 1 : 1;
  const stepX = chartWidth / divisor;
  const barWidth = Math.min(24, stepX * 0.4);

  // Mapear pontos para coordenadas SVG (mensal e acumulado)
  const coords = pontos.map((p, idx) => {
    const x = paddingLeft + (idx / divisor) * chartWidth;
    const yMensal = height - paddingBottom - (p.valorMensal / yMax) * chartHeight;
    const yAcumulado = height - paddingBottom - (p.valorAcumulado / yMax) * chartHeight;
    return { ...p, x, yMensal, yAcumulado };
  });

  // Caminho da Linha Acumulada (Única cor azul escuro)
  let linePathData = '';
  if (coords.length > 0) {
    linePathData = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.yAcumulado}`).join(' ');
  }

  // Gerar linhas de grade horizontais (Y)
  const gridLines = [];
  for (let i = 0; i <= steps; i++) {
    const val = stepValue * i;
    const y = height - paddingBottom - (val / yMax) * chartHeight;
    gridLines.push({ val, y });
  }

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm mb-6">
      {/* Título do Gráfico */}
      <h3 className="text-center text-sm md:text-base font-bold text-gray-700 mb-6">
        Projeção de Economias Encaminhadas para Tratamento
      </h3>

      {/* SVG do Gráfico - Oculta barra de rolagem horizontal */}
      <div 
        className="relative overflow-x-auto w-full scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="min-w-[1000px] relative">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto select-none overflow-visible">
            {/* Linhas de Grade e Eixo Y */}
            {gridLines.map((line, idx) => (
              <g key={idx}>
                {/* Linha horizontal */}
                <line
                  x1={paddingLeft}
                  y1={line.y}
                  x2={width - paddingRight}
                  y2={line.y}
                  stroke="#E2E8F0"
                  strokeWidth="1"
                  strokeDasharray={idx === 0 ? '0' : '4 4'}
                />
                {/* RÃ³tulo do Eixo Y */}
                <text
                  x={paddingLeft - 12}
                  y={line.y + 4}
                  textAnchor="end"
                  className="font-mono text-[11px] font-bold text-gray-400"
                >
                  {line.val === 0 ? '-' : line.val.toLocaleString('pt-BR')}
                </text>
              </g>
            ))}

            {/* Linha de Base do Eixo X */}
            <line
              x1={paddingLeft}
              y1={height - paddingBottom}
              x2={width - paddingRight}
              y2={height - paddingBottom}
              stroke="#CBD5E1"
              strokeWidth="1.5"
            />

            {/* 1. RENDERIZAR COLUNAS (VALORES MENSAIS) */}
            {coords.map((pt, idx) => {
              if (pt.valorMensal === 0) return null;
              
              const barHeight = height - paddingBottom - pt.yMensal;
              const barColor = pt.isConsolidado ? '#10B981' : '#94A3B8';

              return (
                <rect
                  key={`bar-${idx}`}
                  x={pt.x - barWidth / 2}
                  y={pt.yMensal}
                  width={barWidth}
                  height={barHeight}
                  fill={barColor}
                  rx={3}
                  ry={3}
                  className="transition-all duration-150"
                />
              );
            })}

            {/* 2. RENDERIZAR LINHA ACUMULADA (Total - Acumulado em azul escuro) */}
            {linePathData && (
              <path
                d={linePathData}
                fill="none"
                stroke="#0C336A"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Linha de guia vertical no Hover */}
            {hoveredIdx !== null && coords[hoveredIdx] && (
              <line
                x1={coords[hoveredIdx].x}
                y1={paddingTop}
                x2={coords[hoveredIdx].x}
                y2={height - paddingBottom}
                stroke="#3B82F6"
                strokeWidth="1.5"
                strokeDasharray="3 3"
                className="pointer-events-none"
              />
            )}

            {/* CÃ­rculos dos Pontos do Acumulado */}
            {coords.map((pt, idx) => {
              const isHovered = hoveredIdx === idx;
              const dotRadius = isHovered ? 6.5 : 4;
              const strokeColor = isHovered ? '#3B82F6' : '#FFFFFF';
              const strokeWidth = isHovered ? 3 : 1.5;

              // NÃ£o renderiza cÃ­rculo se nÃ£o houver valor acumulado no inÃ­cio
              if (pt.valorAcumulado === 0) return null;

              return (
                <circle
                  key={`dot-${idx}`}
                  cx={pt.x}
                  cy={pt.yAcumulado}
                  r={dotRadius}
                  fill="#0C336A"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  className="transition-all duration-150 pointer-events-none"
                />
              );
            })}

            {/* RÃ³tulos numÃ©ricos acima da linha acumulada */}
            {coords.map((pt, idx) => {
              if (pt.valorAcumulado === 0) return null;
              return (
                <text
                  key={`lbl-ac-${idx}`}
                  x={pt.x}
                  y={pt.yAcumulado - 8}
                  textAnchor="middle"
                  className="text-[9px] font-black fill-[#0C336A]"
                >
                  {pt.valorAcumulado.toLocaleString('pt-BR')}
                </text>
              );
            })}

            {/* RÃ³tulos numÃ©ricos acima das colunas mensais (evitando sobreposiÃ§Ã£o direta) */}
            {coords.map((pt, idx) => {
              if (pt.valorMensal === 0) return null;
              
              // Se o valor acumulado for exatamente o mensal, jÃ¡ mostramos acima (evita sobreposiÃ§Ã£o)
              const showMensal = pt.valorAcumulado !== pt.valorMensal && Math.abs(pt.yMensal - pt.yAcumulado) > 14;
              if (!showMensal) return null;

              return (
                <text
                  key={`lbl-ms-${idx}`}
                  x={pt.x}
                  y={pt.yMensal - 6}
                  textAnchor="middle"
                  className="text-[8px] font-bold fill-gray-500"
                >
                  {pt.valorMensal.toLocaleString('pt-BR')}
                </text>
              );
            })}

            {/* RÃ³tulos do Eixo X */}
            {coords.map((pt, idx) => {
              return (
                <text
                  key={`axis-x-${idx}`}
                  x={pt.x}
                  y={height - paddingBottom + 20}
                  textAnchor="middle"
                  className={`text-[10px] ${pt.isConsolidado ? 'text-emerald-700 font-bold' : 'text-gray-500 font-medium'}`}
                >
                  {pt.label}
                </text>
              );
            })}

            {/* Ãreas InvisÃ­veis de InteraÃ§Ã£o para Hover (Colunas) */}
            {coords.map((pt, idx) => {
              const colWidth = chartWidth / (pontos.length - 1);
              const xStart = pt.x - colWidth / 2;
              return (
                <rect
                  key={`hover-${idx}`}
                  x={idx === 0 ? paddingLeft : xStart}
                  y={paddingTop}
                  width={idx === 0 || idx === pontos.length - 1 ? colWidth / 2 : colWidth}
                  height={chartHeight}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                />
              );
            })}
          </svg>

          {/* Tooltip DinÃ¢mico Customizado */}
          {hoveredIdx !== null && coords[hoveredIdx] && (
            <div
              className="absolute bg-slate-900/95 text-white p-3 rounded-2xl shadow-xl text-xs z-50 pointer-events-none transition-all duration-150 border border-slate-700 backdrop-blur-sm"
              style={{
                left: `${(coords[hoveredIdx].x / width) * 100}%`,
                top: `${((coords[hoveredIdx].yAcumulado < 120 ? coords[hoveredIdx].yAcumulado + 15 : coords[hoveredIdx].yAcumulado - 95) / height) * 100}%`,
                transform: hoveredIdx === coords.length - 1 ? 'translate(-95%, 0)' : (hoveredIdx === 0 ? 'translate(-5%, 0)' : 'translate(-50%, 0)'),
                width: 'max-content', minWidth: '200px'
              }}
            >
              <p className="font-bold text-[10px] uppercase text-gray-400 mb-1">
                {coords[hoveredIdx].tooltipLabel}
              </p>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-300">Mensal ({coords[hoveredIdx].isConsolidado ? 'Realizado' : 'Projetado'}):</span>
                <span className={`font-mono font-black text-xs ${coords[hoveredIdx].isConsolidado ? 'text-emerald-400' : 'text-slate-300'}`}>
                  {coords[hoveredIdx].valorMensal === 0 ? '-' : coords[hoveredIdx].valorMensal.toLocaleString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-300">Acumulado Total:</span>
                <span className="font-mono font-black text-sm text-blue-400">
                  {coords[hoveredIdx].valorAcumulado.toLocaleString('pt-BR')}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-slate-800">
                <span
                  className={`w-2 h-2 rounded-full ${coords[hoveredIdx].isConsolidado ? 'bg-emerald-500' : 'bg-slate-400'}`}
                ></span>
                <span className="font-bold text-[9px] uppercase tracking-wider text-gray-400">
                  {coords[hoveredIdx].isConsolidado ? 'Consolidado' : 'ProjeÃ§Ã£o'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legenda do GrÃ¡fico */}
      <div className="flex justify-center flex-wrap items-center gap-6 mt-4 text-[10px] md:text-[11px] font-bold text-gray-500 uppercase tracking-wider">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-[#10B981] rounded-sm"></span>
          <span>Total Encaminhado - Mensal</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-[#94A3B8] rounded-sm"></span>
          <span>Total ProjeÃ§Ã£o - Mensal</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-6 h-1 bg-[#0C336A] rounded"></span>
          <span>Total - Acumulado</span>
        </div>
      </div>
    </div>
  );
}




